// Что записать из админки: справочник людей, команды, роли и привязка аккаунтов. Чистые функции —
// экран собирает черновик и показывает результат. Спека: docs/superpowers/specs/2026-09-13-people-accounts-roles-design.md
import type { Change, Op, Row } from '@core/store/model';
import type { Role } from '@core/auth/roles';
import { paletteColorOf } from '@core/palette';

export type Person = Row<'person'>;
export type Profile = Row<'profile'>;
export type Team = Row<'team'>;
export type TeamMember = Row<'team_member'>;
export type Planned = { changes: Change[]; op: Op };
export type Problem = { error: string };

export type PersonDraft = { id: string | null; name: string; color: string; is_active: boolean; is_assignable: boolean };

/** Одна колонка вместо двух галочек: человек на плане — исполнитель, только автор вопросов, или ушёл. */
export const ON_PLAN = ['assignable', 'questions', 'left'] as const;
export type OnPlan = typeof ON_PLAN[number];
export const ON_PLAN_LABEL: Record<OnPlan, string> = { assignable: 'Assignable', questions: 'Questions only', left: 'Left' };
export const ON_PLAN_HINT = 'Assignable — a row on People and a place in the assignee list. Questions only — can ask and answer, but is not assigned. Left — gone from the plan; past work keeps the name.';
export const onPlanOf = (p: Pick<Person, 'is_active' | 'is_assignable'>): OnPlan => (!p.is_active ? 'left' : p.is_assignable ? 'assignable' : 'questions');
export const flagsOf = (v: OnPlan): Pick<PersonDraft, 'is_active' | 'is_assignable'> => ({ is_active: v !== 'left', is_assignable: v === 'assignable' });

export { ROLES, ROLE_LABEL, ROLE_HINT, type Role } from '@core/auth/roles';

export const personDraftOf = (p: Person): PersonDraft =>
  ({ id: p.id, name: p.name, color: p.color, is_active: p.is_active, is_assignable: p.is_assignable });
/** Новый человек из админки: цвет по номеру в списке, как у бэкфилла палитры. */
export const newPersonDraft = (name: string, count: number): PersonDraft =>
  ({ id: null, name, color: paletteColorOf(String(count)), is_active: true, is_assignable: true });

const clean = (s: string) => s.trim();

/** Похожие имена — защита от четвёртого «Tom»: точное совпадение и вхождение без регистра. */
export function similarPeople(name: string, people: Iterable<Person>, limit = 5): Person[] {
  const q = clean(name).toLowerCase();
  if (q.length < 2) return [];
  return [...people]
    .filter((p) => p.name.toLowerCase().includes(q) || q.includes(p.name.toLowerCase()))
    .sort((a, b) => Number(b.name.toLowerCase() === q) - Number(a.name.toLowerCase() === q) || a.name.localeCompare(b.name))
    .slice(0, limit);
}

export function planPerson(d: PersonDraft, was: Person | undefined, people: Iterable<Person>): Planned | Problem {
  const name = clean(d.name);
  if (!name) return { error: 'Name is required' };
  if ([...people].some((p) => p.id !== d.id && p.name.toLowerCase() === name.toLowerCase())) {
    return { error: 'Someone with this name is already here' };
  }
  const fields = { name, color: d.color, is_active: d.is_active, is_assignable: d.is_assignable };
  if (!was) return { changes: [{ table: 'person', insert: fields }], op: { kind: 'add_person', name } };
  const same = was.name === name && was.color === d.color && was.is_active === d.is_active && was.is_assignable === d.is_assignable;
  if (same) return { changes: [], op: { kind: 'noop' } };
  return { changes: [{ table: 'person', id: was.id, set: fields, expect_updated_at: was.updated_at }], op: { kind: 'edit_person', name } };
}

/** Человек на лету из окна вопроса: назначать его нельзя, пока админ не разрешит — то же правило в базе. */
export function planQuickPerson(name: string, color: string, people: Iterable<Person>): Planned | Problem {
  return planPerson({ id: null, name, color, is_active: true, is_assignable: false }, undefined, people);
}

/** Что уйдёт вместе с человеком: назначения, отпуска, участие в командах; авторство вопросов обнулится. */
export function personImpact(id: string, o: {
  assignees: Iterable<{ person_id: string }>; vacations: Iterable<{ person_id: string }>;
  members: Iterable<{ person_id: string }>; profiles: Iterable<Profile>;
}): { tasks: number; vacations: number; teams: number; accounts: number } {
  const count = (rows: Iterable<{ person_id: string }>) => [...rows].filter((r) => r.person_id === id).length;
  return {
    tasks: count(o.assignees), vacations: count(o.vacations), teams: count(o.members),
    accounts: [...o.profiles].filter((p) => p.person_id === id).length,
  };
}

type Versioned = { id: string; updated_at: string };
type Owned = Versioned & { person_id: string };

/**
 * Слить дубль в человека одной транзакцией: назначения, отпуска, команды, авторство вопросов и ответов
 * и аккаунт переезжают на цель, дубль удаляется. Где цель уже стоит (та же задача, та же команда) —
 * строка дубля просто уходит, двойника не будет. Два аккаунта на одного человека — отказ: сначала отвязать.
 */
export function planMerge(from: Person, into: Person, o: {
  assignees: Iterable<Owned & { task_id: string }>; vacations: Iterable<Owned>; members: Iterable<Owned & { team_id: string }>;
  questions: Iterable<Versioned & { author_person_id: string | null }>; answers: Iterable<Versioned & { author_person_id: string | null }>;
  profiles: Iterable<Profile>;
}): Planned | Problem {
  if (from.id === into.id) return { error: 'Pick a different person' };
  const fromAccount = [...o.profiles].find((p) => p.person_id === from.id);
  const intoAccount = [...o.profiles].find((p) => p.person_id === into.id);
  if (fromAccount && intoAccount) return { error: `Both are linked to accounts (${fromAccount.email}, ${intoAccount.email}) — unlink one first` };
  const changes: Change[] = [];
  const move = <T extends Owned>(table: 'task_assignee' | 'team_member' | 'vacation', rows: Iterable<T>, sameSlot: (a: T, b: T) => boolean) => {
    const all = [...rows];
    for (const r of all.filter((x) => x.person_id === from.id)) {
      const taken = all.some((x) => x.person_id === into.id && sameSlot(x, r));
      if (taken) changes.push({ table, id: r.id, delete: true });
      else changes.push({ table, id: r.id, set: { person_id: into.id }, expect_updated_at: r.updated_at });
    }
  };
  move('task_assignee', o.assignees, (a, b) => a.task_id === b.task_id);
  move('team_member', o.members, (a, b) => a.team_id === b.team_id);
  move('vacation', o.vacations, () => false);
  for (const q of [...o.questions].filter((x) => x.author_person_id === from.id)) changes.push({ table: 'question', id: q.id, set: { author_person_id: into.id }, expect_updated_at: q.updated_at });
  for (const a of [...o.answers].filter((x) => x.author_person_id === from.id)) changes.push({ table: 'answer', id: a.id, set: { author_person_id: into.id }, expect_updated_at: a.updated_at });
  if (fromAccount) changes.push({ table: 'profile', id: fromAccount.id, set: { person_id: into.id }, expect_updated_at: fromAccount.updated_at });
  changes.push({ table: 'person', id: from.id, delete: true });
  return { changes, op: { kind: 'merge_people', from: from.name, into: into.name, moved: changes.length - 1 } };
}

export function planTeam(name: string, was: Team | undefined, teams: Iterable<Team>): Planned | Problem {
  const n = clean(name);
  if (!n) return { error: 'Name is required' };
  if ([...teams].some((t) => t.id !== was?.id && t.name.toLowerCase() === n.toLowerCase())) {
    return { error: 'A team with this name is already here' };
  }
  if (!was) {
    const position = Math.max(-1, ...[...teams].map((t) => t.position)) + 1;
    return { changes: [{ table: 'team', insert: { name: n, position } }], op: { kind: 'add_team', name: n } };
  }
  if (was.name === n) return { changes: [], op: { kind: 'noop' } };
  return { changes: [{ table: 'team', id: was.id, set: { name: n }, expect_updated_at: was.updated_at }], op: { kind: 'rename_team', name: n } };
}

/** Состав команды целиком: недостающих добавляем, лишних убираем, позиции по порядку списка. */
export function planTeamMembers(teamId: string, personIds: string[], current: Iterable<TeamMember>): Planned {
  const mine = [...current].filter((m) => m.team_id === teamId);
  const changes: Change[] = [];
  for (const m of mine) if (!personIds.includes(m.person_id)) changes.push({ table: 'team_member', id: m.id, delete: true });
  personIds.forEach((person_id, position) => {
    const was = mine.find((m) => m.person_id === person_id);
    if (!was) changes.push({ table: 'team_member', insert: { team_id: teamId, person_id, position } });
    else if (was.position !== position) changes.push({ table: 'team_member', id: was.id, set: { position }, expect_updated_at: was.updated_at });
  });
  return { changes, op: { kind: 'edit_team', team: teamId, members: personIds.length } };
}

/**
 * Роль и человек у аккаунта. Свою роль не меняем: одним кликом можно закрыть себе вход, и вернуть
 * его сможет только другой админ или SQL.
 */
export function planAccount(p: Profile, next: { role: Role; person_id: string | null }, meId: string, profiles: Iterable<Profile>): Planned | Problem {
  if (p.id === meId && next.role !== p.role) return { error: 'You cannot change your own role' };
  if (next.person_id) {
    const busy = [...profiles].find((x) => x.id !== p.id && x.person_id === next.person_id);
    if (busy) return { error: `Already linked to ${busy.email}` };
  }
  if (p.role === next.role && (p.person_id ?? null) === next.person_id) return { changes: [], op: { kind: 'noop' } };
  const kind = p.role !== next.role ? 'set_role' : 'link_account';
  return {
    changes: [{ table: 'profile', id: p.id, set: { role: next.role, person_id: next.person_id }, expect_updated_at: p.updated_at }],
    op: { kind, email: p.email, role: next.role },
  };
}

/** Общий порядок проектов по умолчанию: строка заведена миграцией, меняем массив целиком — правит только админ, конфликтовать некому. */
export function planDefaultOrder(ids: string[], row: { id: string; updated_at: string } | undefined): Planned | Problem {
  if (!row) return { error: 'The default order is missing in this database' };
  return { changes: [{ table: 'shared_setting', id: row.id, set: { value: ids }, expect_updated_at: row.updated_at }], op: { kind: 'set_default_order', projects: ids.length } };
}

/** Общая настройка «роль новичка»: строка заведена миграцией, меняем только значение. */
export function planNewAccountRole(role: Role, row: { id: string; value: unknown; updated_at: string } | undefined): Planned | Problem {
  if (!row) return { error: 'The setting is missing in this database' };
  if (row.value === role) return { changes: [], op: { kind: 'noop' } };
  return { changes: [{ table: 'shared_setting', id: row.id, set: { value: role }, expect_updated_at: row.updated_at }], op: { kind: 'set_new_account_role', role } };
}

export const isProblem = (r: Planned | Problem): r is Problem => 'error' in r;
/** Экран записывает план или показывает, почему нельзя; true — прошло (или нечего писать). */
export type Runner = (planned: Planned | Problem) => Promise<boolean>;

// ---------------------------------------------------------------- таблица: строки, поиск, сортировка

export type PeopleSortKey = 'name' | 'account' | 'role' | 'plan';
export type PeopleSort = { key: PeopleSortKey; dir: 'asc' | 'desc' };
/** Строка таблицы: человек с аккаунтом, человек без аккаунта или аккаунт без человека. */
export type AdminRow = { key: string; person: Person | null; account: Profile | null };

const norm = (s: string | null | undefined) => (s ?? '').toLowerCase();
/** Поиск по подстроке без регистра в любом из полей. */
export const matches = (query: string, ...fields: (string | null | undefined)[]): boolean => {
  const q = norm(query).trim();
  return q === '' || fields.some((f) => norm(f).includes(q));
};

const ROLE_RANK: Record<string, number> = { admin: 0, member: 1, viewer: 2, none: 3 };
const PLAN_RANK: Record<OnPlan, number> = { assignable: 0, questions: 1, left: 2 };

/** Строки справочника; по умолчанию: люди на плане по имени, ушедшие ниже, аккаунты без человека в конце. */
export function adminRows(people: Iterable<Person>, profiles: Iterable<Profile>, query: string, sort: PeopleSort | null): AdminRow[] {
  const byPerson = new Map<string, Profile>();
  const orphans: Profile[] = [];
  for (const p of profiles) { if (p.person_id) byPerson.set(p.person_id, p); else orphans.push(p); }
  const rows: AdminRow[] = [
    ...[...people].map((person) => ({ key: person.id, person, account: byPerson.get(person.id) ?? null })),
    ...orphans.map((account) => ({ key: account.id, person: null, account })),
  ].filter((r) => matches(query, r.person?.name, r.account?.email, r.account?.name));
  const key = (r: AdminRow): string | number => {
    switch (sort?.key) {
      case 'account': return r.account?.email ?? '\uffff';
      case 'role': return r.account ? ROLE_RANK[r.account.role] ?? 9 : 9;
      case 'plan': return r.person ? PLAN_RANK[onPlanOf(r.person)] : 9;
      default: return r.person ? norm(r.person.name) : '\uffff' + norm(r.account?.email);
    }
  };
  const dir = sort?.dir === 'desc' ? -1 : 1;
  return rows.sort((a, b) => {
    if (!sort) {
      const group = (r: AdminRow) => (!r.person ? 2 : r.person.is_active ? 0 : 1);
      const g = group(a) - group(b); if (g) return g;
    }
    const ka = key(a), kb = key(b);
    const c = ka < kb ? -1 : ka > kb ? 1 : 0;
    return c * dir || norm(a.person?.name ?? a.account?.email).localeCompare(norm(b.person?.name ?? b.account?.email));
  });
}

/** Аккаунты без человека — сюда попадает тот, кто вошёл впервые. */
export const unlinkedAccounts = (profiles: Iterable<Profile>): Profile[] =>
  [...profiles].filter((p) => !p.person_id).sort((a, b) => a.email.localeCompare(b.email));

/** Люди без аккаунта — кандидаты для привязки. */
export const linkCandidates = (people: Iterable<Person>, profiles: Iterable<Profile>): Person[] => {
  const taken = new Set([...profiles].map((p) => p.person_id).filter(Boolean));
  return [...people].filter((p) => p.is_active && !taken.has(p.id)).sort((a, b) => a.name.localeCompare(b.name));
};

/** Что скажет ×: сколько уйдёт каскадом, одной строкой. */
export const impactLabel = (i: { tasks: number; vacations: number; teams: number; accounts?: number }): string => {
  const parts = [i.tasks && `${i.tasks} assignment${i.tasks === 1 ? '' : 's'}`, i.vacations && `${i.vacations} vacation${i.vacations === 1 ? '' : 's'}`, i.teams && `${i.teams} team${i.teams === 1 ? '' : 's'}`, i.accounts && `${i.accounts} account unlinked`].filter(Boolean);
  return parts.length ? ` with ${parts.join(', ')}` : '';
};
